import {
  Box,
  Stack,
  Tab,
  Tabs,
  tabsClasses,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  getProjectDetails,
  getProjectUpdates,
  Project,
  ProjectUpdates,
  qk,
} from 'api'
import { paths } from 'app/routes'
import {
  IconArrowFull,
  IconClock,
  IconLink,
  IconSearch,
  IconSettings,
  IconTranslation,
  IconUser,
} from 'assets/icons'
import { Toolbar } from 'components/toolbar'
import { NavigationItem } from 'components/toolbar/navigation-item'
import { Loader } from 'elements/feedback'
import { Link } from 'elements/navigation'
import { Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { generatePath, useLocation } from 'react-router-dom'
import { Outlet, useOutletContext, useParams } from 'react-router-dom'

const DotDivider = () => (
  <Box component="span" alignSelf="center" mx={1} color="text.secondary">
    •
  </Box>
)

export const ProjectDetailsLayout = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const theme = useTheme()

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { projectId } = useParams() as { projectId: string }

  const { data: updates, refetch: refetchUpdates } = useQuery(
    qk.projects.updates.toKeyWithArgs({ projectId }),
    () => getProjectUpdates({ projectId }),
  )

  const { data: project } = useQuery(
    qk.projects.details.toKeyWithArgs({ projectId }),
    () => getProjectDetails({ projectId }),
    { enabled: updates !== undefined },
  )

  const projectDetailsPages = [
    {
      label: t('projects.overview.overview'),
      to: generatePath(paths.projectOverview, { projectId: projectId }),
      icon: <IconSearch />,
    },
    {
      label: t('projects.terms.terms'),
      to: generatePath(paths.projectTerms, { projectId }),
      icon: <IconTranslation />,
    },
    {
      label: t('projects.users.users'),
      to: generatePath(paths.projectUsers, { projectId }),
      icon: <IconUser />,
    },
    {
      label: t('common.settings'),
      to: generatePath(paths.projectSettings, { projectId }),
      icon: <IconSettings />,
    },
    {
      label: t('common.history'),
      to: generatePath(paths.projectHistory, { projectId }),
      icon: <IconClock />,
    },
  ]

  useEffect(() => {
    const projectOverviewPath = generatePath(paths.projectOverview, {
      projectId: projectId,
    })

    if (pathname === projectOverviewPath) {
      refetchUpdates()
    }
  }, [pathname, refetchUpdates, projectId])

  if (!project || !updates) return <Loader centered />

  const tabs = projectDetailsPages.reduce((result, tab, index) => {
    return [
      ...result,
      <Tab
        key={tab.label}
        component={Link}
        label={tab.label}
        value={tab.to}
        to={tab.to}
        underline="none"
        sx={{ px: 0, minWidth: 'auto' }}
        disableRipple
      />,
      index !== projectDetailsPages.length - 1 && (
        <DotDivider key={tab.to + 'divider'} />
      ),
    ]
  }, [] as Array<React.ReactNode>)

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      <Toolbar
        toolbarChildren={
          <Tabs
            value={location.pathname}
            sx={{
              height: '100%',
              [`& .${tabsClasses.flexContainer}`]: {
                height: '100%',
              },
            }}
          >
            {tabs}
          </Tabs>
        }
        menuDrawerChildren={closeDrawer => (
          <Stack spacing={1}>
            {projectDetailsPages.map(page => (
              <NavigationItem to={page.to} key={page.to} onClick={closeDrawer}>
                <Box display="flex" alignItems="center" py={1}>
                  <Box display="flex" mr={1.5}>
                    {page.icon}
                  </Box>

                  {page.label}
                </Box>
              </NavigationItem>
            ))}
          </Stack>
        )}
        backLink={
          <>
            <Link
              to={paths.projects}
              underline="none"
              sx={{ display: 'flex', alignItems: 'center', pl: 4 }}
            >
              <IconArrowFull
                direction="left"
                sx={{ fontSize: '16px', color: 'text.primary' }}
              />

              <Box display="flex" alignItems="center">
                <Typography
                  // eslint-disable-next-line prettier/prettier
                  variant={isDesktop ? 'body3' : (isMobile ? 'caption2' : 'caption1')}
                  color="text.primary"
                  ml={1}
                  fontWeight={600}
                  noWrap
                  maxWidth={300}
                >
                  {project.name}
                </Typography>

                {project.linkedProject && (
                  <Tooltip
                    title={
                      <Typography variant="caption2">
                        {project.linkedProject.name}
                      </Typography>
                    }
                  >
                    <Box display="flex">
                      <IconLink
                        sx={{
                          color: 'text.primary',
                          fontSize: '20px',
                          ml: 0.5,
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Link>
          </>
        }
      />

      <Box
        overflow="auto"
        display="flex"
        flexDirection="column"
        flex="auto"
        position="relative"
      >
        <Helmet
          title={
            projectDetailsPages.find(page => page.to === location.pathname)
              ?.label
          }
        />

        <Suspense fallback={null}>
          <Outlet context={{ project, updates }} />
        </Suspense>
      </Box>
    </Box>
  )
}

export const useProjectDetailsContext = () => {
  return useOutletContext<{ project: Project; updates: ProjectUpdates }>()
}
